import { useCallback, useEffect, useRef, useState } from 'react'
import { Plus, Pencil, Trash2, X, Camera, UploadCloud, Loader2 } from 'lucide-react'
import type { MemberDto, AppReleaseDto } from '@familynest/shared'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../store/auth'
import { PERSON_COLORS } from '../theme/persons'
import { Bell } from 'lucide-react'
import { Avatar, Button, useBackdropClose } from './ui'
import { useDialogs } from './ui/dialogs'
import { AppFooter } from './AppFooter'
import { enablePush, disablePush, pushPermission, pushSupported } from '../lib/push'
import { PUSH_DISMISS_KEY } from './PushBanner'

const ROLE_LABEL: Record<string, string> = { adult: 'Erwachsener', child: 'Kind', guest: 'Wand-Tablet' }
const field = 'rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)] w-full'
const fieldStyle = { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' } as const

export function MembersScreen({ onChanged }: { onChanged: () => void }): React.JSX.Element {
  const { member: me } = useAuth()
  const dialogs = useDialogs()
  const [members, setMembers] = useState<MemberDto[]>([])
  const [dialog, setDialog] = useState<{ editing: MemberDto | null } | null>(null)

  const load = useCallback(async () => setMembers(await api.listMembers()), [])
  useEffect(() => {
    void load()
  }, [load])

  async function remove(m: MemberDto): Promise<void> {
    if (!(await dialogs.confirm({ title: 'Person löschen', message: `„${m.name}" wirklich löschen?`, confirmLabel: 'Löschen', danger: true }))) return
    try {
      await api.deleteMember(m.id)
      await load()
      onChanged()
    } catch {
      await dialogs.alert({ title: 'Löschen nicht möglich', message: 'z. B. letzter Erwachsener oder du selbst.' })
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--color-ink)' }}>
          Mitglieder
        </h1>
        <Button onClick={() => setDialog({ editing: null })}>
          <Plus className="size-4" /> Person hinzufügen
        </Button>
      </div>

      <ul className="flex flex-col gap-2">
        {members.map((m) => (
          <li key={m.id} className="flex items-center gap-3 rounded-[var(--radius-card)] border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
            <Avatar name={m.name} color={m.color} size={40} src={m.avatarUrl} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium" style={{ color: 'var(--color-ink)' }}>{m.name}</span>
                <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-muted)' }}>{ROLE_LABEL[m.role]}</span>
              </div>
              <div className="truncate text-xs" style={{ color: 'var(--color-muted)' }}>
                {m.role !== 'guest' && `${m.email} · ${m.birthdate}`}
              </div>
            </div>
            {m.role !== 'guest' && (
              <>
                <button onClick={() => setDialog({ editing: m })} className="rounded p-1.5 hover:bg-black/5" aria-label="Bearbeiten">
                  <Pencil className="size-4" style={{ color: 'var(--color-muted)' }} />
                </button>
                {m.id !== me?.id && (
                  <button onClick={() => remove(m)} className="rounded p-1.5 hover:bg-black/5" aria-label="Löschen">
                    <Trash2 className="size-4" style={{ color: 'var(--color-muted)' }} />
                  </button>
                )}
              </>
            )}
          </li>
        ))}
      </ul>

      <PushSettings />

      <AppUpdateAdmin />

      <AppFooter />

      {dialog && (
        <MemberDialog
          editing={dialog.editing}
          onClose={() => {
            setDialog(null)
            void load()
            onChanged()
          }}
          onSaved={() => {
            setDialog(null)
            void load()
            onChanged()
          }}
        />
      )}
    </div>
  )
}

/** Admin-Sektion (nur für das Update-Admin-Konto): neue App-Version (APK) hochladen (ADR-051). */
/**
 * Benachrichtigungs-Einstellungen (Niedrig): Push jederzeit wieder aktivierbar —
 * auch nachdem der Banner mit „Später" weggeklickt wurde (setzt den Merker zurück)
 * — und abschaltbar. Schließt den toten `disablePush`-Pfad.
 */
function PushSettings(): React.JSX.Element | null {
  const dialogs = useDialogs()
  const [perm, setPerm] = useState(() => pushPermission())
  const [busy, setBusy] = useState(false)
  if (!pushSupported()) return null

  async function enable(): Promise<void> {
    setBusy(true)
    localStorage.removeItem(PUSH_DISMISS_KEY)
    const res = await enablePush()
    setBusy(false)
    setPerm(pushPermission())
    if (res === 'error') await dialogs.alert({ title: 'Aktivieren fehlgeschlagen', message: 'Bitte später erneut versuchen.' })
  }
  async function disable(): Promise<void> {
    setBusy(true)
    await disablePush()
    setBusy(false)
    setPerm(pushPermission())
  }

  const active = perm === 'granted'
  return (
    <div className="mt-6 rounded-[var(--radius-card)] border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
      <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>
        <Bell className="size-4" /> Benachrichtigungen
      </h2>
      <p className="mb-3 text-xs" style={{ color: 'var(--color-muted)' }}>
        {active ? 'Aktiv — du bekommst neue Nachrichten und Erinnerungen aufs Gerät.' : perm === 'denied' ? 'Im Browser/Gerät blockiert — bitte dort erlauben.' : 'Aus. Aktiviere, um nichts zu verpassen.'}
      </p>
      <div className="flex justify-end gap-2">
        {active ? (
          <Button variant="ghost" onClick={disable} disabled={busy}>Abschalten</Button>
        ) : (
          <Button onClick={enable} disabled={busy || perm === 'denied'}>Aktivieren</Button>
        )}
      </div>
    </div>
  )
}

function AppUpdateAdmin(): React.JSX.Element | null {
  const dialogs = useDialogs()
  const [canUpload, setCanUpload] = useState(false)
  const [live, setLive] = useState<AppReleaseDto | null>(null)
  const [releases, setReleases] = useState<AppReleaseDto[]>([])
  const [file, setFile] = useState<File | null>(null)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    try {
      const latest = await api.latestRelease()
      setCanUpload(latest.canUpload)
      setLive(latest.release)
      if (latest.canUpload) setReleases(await api.listReleases())
    } catch {
      /* ignore */
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  if (!canUpload) return null

  async function upload(): Promise<void> {
    if (!file) return
    setBusy(true)
    setMsg(null)
    try {
      const rel = await api.uploadRelease(file, notes.trim())
      setMsg(`✓ v${rel.versionName} hochgeladen — als Entwurf. Erst „Für alle freigeben" verteilt sie.`)
      setFile(null)
      setNotes('')
      if (fileRef.current) fileRef.current.value = ''
      await load()
    } catch (e) {
      const code = e instanceof ApiError ? e.status : 0
      setMsg(code === 413 ? 'APK zu groß.' : code === 400 ? 'Das ist keine gültige FamilyNest-APK.' : 'Upload fehlgeschlagen.')
    } finally {
      setBusy(false)
    }
  }
  async function publish(id: string, v: string): Promise<void> {
    if (!(await dialogs.confirm({ title: 'Version freigeben', message: `Version ${v} jetzt für alle Familien-Handys freigeben?`, confirmLabel: 'Freigeben' }))) return
    setBusy(true)
    try {
      await api.publishRelease(id)
      setMsg(`✓ v${v} freigegeben — wird jetzt allen angeboten.`)
      await load()
    } finally {
      setBusy(false)
    }
  }
  async function remove(id: string, v: string): Promise<void> {
    if (!(await dialogs.confirm({ title: 'Version löschen', message: `Version ${v} löschen?`, confirmLabel: 'Löschen', danger: true }))) return
    setBusy(true)
    try {
      await api.deleteRelease(id)
      await load()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-6 rounded-[var(--radius-card)] border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
      <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>
        <UploadCloud className="size-4" /> App-Updates (Android)
      </h2>
      <p className="mb-3 text-xs" style={{ color: 'var(--color-muted)' }}>
        Aktuell freigegeben: {live ? `v${live.versionName}` : '—'}. APK hochladen (Version wird automatisch daraus gelesen) — sie wird als **Entwurf** abgelegt und erst nach deiner **Freigabe** an die Familie verteilt.
      </p>
      <div className="flex flex-col gap-2">
        {/* Kein enges `accept`: der macOS-WKWebView-Dateidialog ("Designed for iPad")
            graut .apk aus, weil weder die Endung noch der Android-MIME ein bekannter
            macOS-UTI ist. Der Server validiert ohnehin serverseitig (echte FamilyNest-APK). */}
        <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className={field} style={fieldStyle} />
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Was ist neu? (optional)" rows={2} className={field} style={fieldStyle} />
        {msg && <p className="text-sm" style={{ color: 'var(--color-muted)' }}>{msg}</p>}
        <div className="flex justify-end">
          <Button onClick={upload} disabled={busy || !file}>{busy ? <Loader2 className="size-4 animate-spin" /> : <UploadCloud className="size-4" />} Hochladen</Button>
        </div>
      </div>

      {releases.length > 0 && (
        <div className="mt-4 flex flex-col divide-y" style={{ borderColor: 'var(--color-line)' }}>
          {releases.map((r) => {
            const published = r.publishedAt !== null
            const isLive = live?.id === r.id
            return (
              <div key={r.id} className="flex items-center gap-2 py-2" style={{ borderColor: 'var(--color-line)' }}>
                <span className="text-sm font-medium" style={{ color: 'var(--color-ink)' }}>v{r.versionName}</span>
                {isLive ? (
                  <span className="rounded-full px-2 py-0.5 text-xs text-white" style={{ backgroundColor: 'var(--color-accent)' }}>aktuell freigegeben</span>
                ) : published ? (
                  <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-muted)' }}>freigegeben</span>
                ) : (
                  <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-warning)' }}>Entwurf</span>
                )}
                <span className="ml-auto flex items-center gap-1">
                  {!isLive && (
                    <button onClick={() => publish(r.id, r.versionName)} disabled={busy} className="rounded-lg px-2 py-1 text-xs font-semibold text-white disabled:opacity-50" style={{ backgroundColor: 'var(--color-accent)' }}>
                      Für alle freigeben
                    </button>
                  )}
                  <button onClick={() => remove(r.id, r.versionName)} disabled={busy} className="rounded p-1.5 hover:bg-black/5 disabled:opacity-50" aria-label="Löschen">
                    <Trash2 className="size-4" style={{ color: 'var(--color-muted)' }} />
                  </button>
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function MemberDialog({ editing, onClose, onSaved }: { editing: MemberDto | null; onClose: () => void; onSaved: () => void }): React.JSX.Element {
  const [name, setName] = useState(editing?.name ?? '')
  const [email, setEmail] = useState(editing?.email ?? '')
  const [birthdate, setBirthdate] = useState(editing?.birthdate ?? '')
  const [role, setRole] = useState<'adult' | 'child'>(editing && editing.role !== 'guest' ? editing.role : 'child')
  const [parentTitle, setParentTitle] = useState(editing?.parentTitle ?? '')
  // Kind darf einen eigenen Kalender-Feed (ICS) erzeugen — ADR-086.
  const [feedAllowed, setFeedAllowed] = useState(editing?.calendarFeedAllowed ?? false)
  const [color, setColor] = useState(editing?.color ?? PERSON_COLORS[2])
  const [secret, setSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(editing?.avatarUrl ?? null)
  const fileRef = useRef<HTMLInputElement>(null)

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !editing) return
    setBusy(true)
    setError(null)
    try {
      const updated = await api.uploadAvatar(editing.id, file)
      setAvatarUrl(updated.avatarUrl)
    } catch (err) {
      const code = err instanceof ApiError ? err.message : ''
      setError(
        code === 'file_too_large'
          ? 'Bild ist zu groß (max. 15 MB). Bitte ein kleineres Foto wählen.'
          : code === 'no_image'
            ? 'Das ist keine gültige Bilddatei.'
            : 'Bild konnte nicht hochgeladen werden.',
      )
    } finally {
      setBusy(false)
    }
  }
  async function removeAvatar(): Promise<void> {
    if (!editing) return
    setBusy(true)
    try {
      await api.deleteAvatar(editing.id)
      setAvatarUrl(null)
    } finally {
      setBusy(false)
    }
  }

  const valid =
    name.trim() &&
    /^\S+@\S+\.\S+$/.test(email) &&
    /^\d{4}-\d{2}-\d{2}$/.test(birthdate) &&
    (editing ? true : secret.trim().length > 0)

  async function save(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const pt = role === 'adult' ? (parentTitle.trim() || null) : null
      if (editing) {
        await api.updateMember(editing.id, {
          name: name.trim(),
          email: email.trim(),
          birthdate,
          role,
          color,
          parentTitle: pt,
          ...(role === 'child' ? { calendarFeedAllowed: feedAllowed } : {}),
          ...(secret.trim() ? { secret: secret.trim() } : {}),
        })
      } else {
        await api.createMember({ name: name.trim(), email: email.trim(), birthdate, role, color, parentTitle: pt, secret: secret.trim() })
      }
      onSaved()
    } catch {
      setError('Speichern fehlgeschlagen. Bitte Eingaben prüfen.')
      setBusy(false)
    }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="max-h-[90vh] w-full max-w-md overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>{editing ? 'Person bearbeiten' : 'Neue Person'}</h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5">
            <X className="size-5" style={{ color: 'var(--color-muted)' }} />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          {editing && (
            <div className="flex items-center gap-3">
              <Avatar name={name || editing.name} color={color} size={56} src={avatarUrl} />
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} />
                <Button variant="ghost" onClick={() => fileRef.current?.click()} disabled={busy}>
                  <Camera className="size-4" /> {avatarUrl ? 'Bild ändern' : 'Bild wählen'}
                </Button>
                {avatarUrl && (
                  <Button variant="ghost" onClick={removeAvatar} disabled={busy}>Entfernen</Button>
                )}
              </div>
            </div>
          )}
          <input className={field} style={fieldStyle} placeholder="Vorname" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <input type="email" className={field} style={fieldStyle} placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} />
          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Geburtsdatum</span>
            <input type="date" className={field} style={fieldStyle} value={birthdate} onChange={(e) => setBirthdate(e.target.value)} />
          </label>

          <div className="flex gap-2">
            {(['adult', 'child'] as const).map((r) => (
              <button key={r} type="button" onClick={() => setRole(r)} className="flex-1 rounded-lg px-3 py-2 text-sm" style={role === r ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { backgroundColor: 'var(--color-surface)', color: 'var(--color-ink)', border: '1px solid var(--color-line)' }}>
                {r === 'adult' ? 'Erwachsener' : 'Kind'}
              </button>
            ))}
          </div>

          {role === 'adult' && (
            <label className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--color-muted)' }}>Anrede im Kalender (Dienstplan-Termine)</span>
              <select className={field} style={fieldStyle} value={parentTitle} onChange={(e) => setParentTitle(e.target.value)}>
                <option value="">— (keine)</option>
                <option value="Mama">Mama</option>
                <option value="Papa">Papa</option>
              </select>
            </label>
          )}

          {role === 'child' && editing && (
            <label className="flex items-start gap-2 text-sm" style={{ color: 'var(--color-ink)' }}>
              <input type="checkbox" className="mt-0.5" checked={feedAllowed} onChange={(e) => setFeedAllowed(e.target.checked)} />
              <span>
                Darf den Kalender aufs eigene Handy abonnieren
                <span className="block text-xs" style={{ color: 'var(--color-muted)' }}>
                  {editing.name} kann dann im Profil einen Abo-Link erzeugen (nur eigene Termine). Abschalten macht vorhandene Links sofort ungültig.
                </span>
              </span>
            </label>
          )}

          <div>
            <span className="mb-1 block text-sm" style={{ color: 'var(--color-muted)' }}>Farbe</span>
            <div className="flex flex-wrap gap-2">
              {PERSON_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)} className="size-7 rounded-full" style={{ backgroundColor: c, outline: color === c ? '2px solid var(--color-ink)' : 'none', outlineOffset: 2 }} aria-label={c} />
              ))}
            </div>
          </div>

          <input
            type="password"
            className={field}
            style={fieldStyle}
            placeholder={role === 'child' ? (editing ? 'Neue PIN (leer = unverändert)' : 'PIN') : editing ? 'Neues Passwort (leer = unverändert)' : 'Passwort'}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            autoComplete="new-password"
          />

          {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}

          <div className="mt-1 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
            <Button onClick={save} disabled={busy || !valid}>{editing ? 'Speichern' : 'Anlegen'}</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
